import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@workos-inc/authkit-js';
import { IPerson } from '../../webapp/types';

const AuthContext = createContext<IAuthContext>(null);

export interface IAuthContext {
  user: IPerson;
  isLoading: boolean;
  logout: () => void;
  openLogin: () => void;
  openSignup: () => void;
  getAccessToken: () => Promise<string>;
}

export function useWorkOsAuth(): IAuthContext {
  return useContext(AuthContext);
}

export interface IAuthProviderProps {
  clientId: string;
  children: React.ReactNode;
}

export default function AuthProvider({ clientId, children }: IAuthProviderProps) {
  const clientRef = useRef<Awaited<ReturnType<typeof createClient>>>();
  const [user, setUser] = useState<IPerson>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let disposed = false;

    async function initialize() {
      if (!clientId || clientId === 'client_test') {
        throw new Error('WorkOSClientId has not been configured');
      }

      const client = await createClient(clientId, {
        onRefresh: ({ user: refreshedUser }) => {
          if (!disposed) setUser(mapUser(refreshedUser));
        },
      });

      if (disposed) {
        client.dispose();
        return;
      }

      clientRef.current = client;
      setUser(mapUser(client.getUser()));
      setIsLoading(false);
    }

    initialize().catch(error => {
      console.error('Unable to initialize WorkOS AuthKit', error);
      if (!disposed) setIsLoading(false);
    });

    return () => {
      disposed = true;
      clientRef.current?.dispose();
    };
  }, [clientId]);

  const getClient = () => {
    if (!clientRef.current) throw new Error('WorkOS AuthKit has not finished initializing');
    return clientRef.current;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        logout: () => getClient().signOut({ returnTo: window.location.origin }),
        openLogin: () => getClient().signIn(),
        openSignup: () => getClient().signUp(),
        getAccessToken: () => getClient().getAccessToken(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function mapUser(user: ReturnType<Awaited<ReturnType<typeof createClient>>['getUser']>): IPerson {
  if (!user) return null;

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
  return {
    Email: user.email,
    FirstName: user.firstName ?? '',
    LastName: user.lastName ?? '',
    FullName: fullName,
    Title: '',
  };
}
